import { useState } from "react";
import { useUploadQuestionsExcelMutation } from "../../../slices/excelUploadApiSlice";

const ExcelUploadForm = ({ quizzes = [], subjects = [], onUploadSuccess }) => {
  const [uploadType, setUploadType] = useState("questionBank");
  const [quiz, setQuiz] = useState("");
  const [subject, setSubject] = useState("");
  const [file, setFile] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const [uploadQuestionsExcel] = useUploadQuestionsExcelMutation();

  /**
   * File Selection
   */
  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];

    if (!selectedFile) {
      setFile(null);
      return;
    }

    const extension = selectedFile.name.split(".").pop()?.toLowerCase();

    if (!["xlsx", "xls"].includes(extension)) {
      setError("Only Excel files (.xlsx, .xls) are allowed.");
      e.target.value = "";
      setFile(null);
      return;
    }

    setError("");
    setFile(selectedFile);
  };

  /**
   * Upload Excel
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    setError("");
    setResult(null);

    if (!uploadType) {
      setError("Please select upload type.");
      return;
    }

    if (!subject) {
      setError("Please select subject.");
      return;
    }

    if (uploadType === "quiz" && !quiz) {
      setError("Please select quiz.");
      return;
    }

    if (!file) {
      setError("Please select an Excel file.");
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();

      formData.append("uploadType", uploadType);
      formData.append("subject", subject);
      formData.append("file", file);

      if (uploadType === "quiz") {
        formData.append("quiz", quiz);
      }

      const response = await uploadQuestionsExcel(formData).unwrap();

      setResult(response);

      if (onUploadSuccess) {
        onUploadSuccess(response);
      }

      setFile(null);
      setQuiz("");
      setSubject("");
      setUploadType("questionBank");

      e.target.reset();
    } catch (err) {
      setError(err?.data?.message || "Excel upload failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="excel-upload-form">
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Upload Type</label>

          <select
            value={uploadType}
            onChange={(e) => {
              setUploadType(e.target.value);

              if (e.target.value === "questionBank") {
                setQuiz("");
              }
            }}
          >
            <option value="questionBank">Question Bank</option>

            <option value="quiz">Quiz</option>
          </select>
        </div>

        {uploadType === "quiz" && (
          <div className="form-group">
            <label>Select Quiz</label>

            <select value={quiz} onChange={(e) => setQuiz(e.target.value)}>
              <option value="">Select Quiz</option>

              {quizzes.map((item) => (
                <option key={item._id} value={item._id}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="form-group">
          <label>Select Subject</label>

          <select value={subject} onChange={(e) => setSubject(e.target.value)}>
            <option value="">Select Subject</option>

            {subjects.map((item) => (
              <option key={item._id} value={item._id}>
                {item.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Upload Excel File</label>

          <input type="file" accept=".xlsx,.xls" onChange={handleFileChange} />

          {file && (
            <p>
              <strong>Selected:</strong> {file.name}
            </p>
          )}
        </div>

        {error && <div className="error-message">{error}</div>}

        <button type="submit" disabled={loading}>
          {loading
            ? "Uploading..."
            : uploadType === "quiz"
              ? "Upload to Quiz"
              : "Upload to Question Bank"}
        </button>
      </form>

      {result && (
        <div className="upload-result">
          <h3>Upload Completed</h3>

          <p>Total Records: {result.upload.totalRecords}</p>

          <p>Successful: {result.upload.successfulRecords}</p>

          <p>Failed: {result.upload.failedRecords}</p>
        </div>
      )}
    </div>
  );
};

export default ExcelUploadForm;
